import {createContext, useMemo, useState} from "react";
import {createTheme} from "@mui/material";

export const tokens = (mode: any) => ({
    ...(mode === "dark"
        ? {
            grey: {
                100: "#e0e0e0",
                200: "#c2c2c2",
                300: "#a3a3a3",
                400: "#858585",
                500: "#666666",
                600: "#525252",
                700: "#3d3d3d",
                800: "#292929",
                900: "#141414",
            },
            secondary: {
                100: '#E9E8E9',
                200: '#D0CED0',
                300: '#B7B4B7',
                400: '#9E9A9E',
                500: '#858085',
                600: '#6B676B',
                700: '#514E51',
                800: '#373537',
                900: '#1D1C1D',
            },
            primary: {
                100: '#E1F2FA',
                200: '#BEE2F5',
                300: '#9AD2EF',
                400: '#77C3EA',
                500: '#54B3E4',
                600: '#249DDC',
                700: '#1B7BAD',
                800: '#14597C',
                900: '#0C364C',
            },
            greenAccent: {
                100: "#dbf5ee",
                200: "#b7ebde",
                300: "#94e2cd",
                400: "#70d8bd",
                500: "#4cceac",
                600: "#3da58a",
                700: "#2e7c67",
                800: "#1e5245",
                900: "#0f2922",
            },
            redAccent: {
                100: '#FCDADA',
                200: '#F9B5B5',
                300: '#F58F8F',
                400: '#F26A6A',
                500: '#EF4444',
                600: '#E71414',
                700: '#B30F0F',
                800: '#800B0B',
                900: '#4C0707',
            },
            blueAccent: {
                100: "#e1e2fe",
                200: "#c3c6fd",
                300: "#a4a9fc",
                400: "#868dfb",
                500: "#6870fa",
                600: "#535ac8",
                700: "#3e4396",
                800: "#2a2d64",
                900: "#151632",
            },
        }
        : {
            grey: {
                100: "#141414",
                200: "#292929",
                300: "#3d3d3d",
                400: "#525252",
                500: "#666666",
                600: "#858585",
                700: "#a3a3a3",
                800: "#c2c2c2",
                900: "#e0e0e0"
            },
            secondary: {
                100: '#E9E8E9',
                200: '#D0CED0',
                300: '#B7B4B7',
                400: '#9E9A9E',
                500: '#858085',
                600: '#6B676B',
                700: '#514E51',
                800: '#373537',
                900: '#1D1C1D',
            },
            primary: {
                100: "#E1F2FA",
                200: "#BEE2F5",
                300: "#9AD2EF",
                400: "#77C3EA",
                500: "#54B3E4",
                600: "#249DDC",
                700: "#1B7BAD",
                800: "#14597C",
                900: "#0C364C"
            },
            greenAccent: {
                100: "#0f2922",
                200: "#1e5245",
                300: "#2e7c67",
                400: "#3da58a",
                500: "#4cceac",
                600: "#70d8bd",
                700: "#94e2cd",
                800: "#b7ebde",
                900: "#dbf5ee"
            },
            redAccent: {
                100: "#FCDADA",
                200: "#F9B5B5",
                300: "#F58F8F",
                400: "#F26A6A",
                500: "#EF4444",
                600: "#E71414",
                700: "#B30F0F",
                800: "#800B0B",
                900: "#4C0707"
            },
            blueAccent: {
                100: "#151632",
                200: "#2a2d64",
                300: "#3e4396",
                400: "#535ac8",
                500: "#6870fa",
                600: "#868dfb",
                700: "#a4a9fc",
                800: "#c3c6fd",
                900: "#e1e2fe"
            }
        })
});

export const themeSettings = (mode: any) => {
    const colors = tokens(mode);
    return {
        palette: {
            mode: mode,
            ...(mode === "dark"
                ? {
                    primary: {
                        main: colors.primary[700]
                    },
                    secondary: {
                        main: colors.greenAccent[500]
                    },
                    neutral: {
                        dark: colors.grey[700],
                        main: colors.grey[500],
                        light: colors.grey[100]
                    },
                    background: {
                        default: colors.grey[200]
                    }
                }
                : {
                    // palette values for light mode
                    primary: {
                        main: colors.primary[500]
                    },
                    secondary: {
                        main: colors.secondary[100]
                    },
                    neutral: {
                        dark: colors.grey[700],
                        main: colors.grey[500],
                        light: colors.grey[100]
                    },
                    background: {
                        default: "#fcfcfc"
                    }
                })
        },
        typography: {
            fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
            fontSize: 12,
            h1: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 40
            },
            h2: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 26
            },
            h3: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 24
            },
            h4: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 20
            },
            h5: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 16
            },
            h6: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 14
            }
        }
    };
};

// context for color mode
export const ColorModeContext = createContext({
    toggleColorMode: () => {
    }
});

export const useMode = () => {
    const [mode, setMode] = useState("light");

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () =>
                setMode((prev) => (prev === "light" ? "dark" : "light"))
        }),
        []
    );

    const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
    return [theme, colorMode];
};
